/* Licenses — admin license management table. Pure-presentational; the
   parent provides data + actions. Layout: sticky filter bar (search +
   status + tier), table with status/validity chips and masked license
   key (copy on click), per-row Suspend/Activate + expand-in-place
   detail panel for usage stats. */
import React, { useState } from "react";
import { P, FC } from "../../shared/constants";
import DataTable from "../components/DataTable";
import EmptyState from "../components/EmptyState";
import ErrorState from "../components/ErrorState";
import Skeleton from "../components/Skeleton";
import Chip from "../components/Chip";
import Modal from "../components/Modal";
import { Search, Plus, Refresh, Pause, Play, Copy, CreditCard, Trash, AlertTriangle } from "../icons";
var E = React.createElement;

function maskKey(k){
  if(!k) return "—";
  var s = String(k);
  if(s.length <= 8) return s;
  return s.slice(0,4) + "…" + s.slice(-4);
}

function validityChip(license){
  /* Prefer v1.1's explicit expiresAt; fall back to annualResetDate so
     licenses that haven't been migrated yet still surface a real date. */
  var iso = license.expiresAt || license.annualResetDate;
  if(!iso) return E(Chip,{variant:"slate",size:"sm"}, "Perpetual");
  var ms = new Date(iso).getTime() - Date.now();
  var days = Math.ceil(ms / 86400000);
  if(days < 0)  return E(Chip,{variant:"coral",size:"sm",title:iso}, "Expired");
  if(days <=14) return E(Chip,{variant:"amber",size:"sm",title:iso}, days+"d left");
  return E(Chip,{variant:"emerald",size:"sm",title:iso}, days+"d left");
}

export default function Licenses(props){
  var licenses = props.licenses || [];
  var loading  = !!props.loading;
  var error    = props.error || null;

  var st = useState({ q:"", status:"", tier:"" });
  var f = st[0], setF = st[1];
  var openRow = useState(null);
  var openId = openRow[0], setOpenId = openRow[1];
  /* PR-5.9: typed-confirmation modal state for hard-delete. Holds the
     license being deleted + the user's typed input. Confirmation
     unlocks only when the typed string matches the license code or
     "DELETE" — destructive action, intentional friction. */
  var delState = useState(null);
  var delTarget = delState[0], setDelTarget = delState[1];
  var typedState = useState("");
  var typed = typedState[0], setTyped = typedState[1];
  var delBusyState = useState(false);
  var delBusy = delBusyState[0], setDelBusy = delBusyState[1];

  function update(patch){ setF(Object.assign({}, f, patch)); }

  var rows = licenses.filter(function(l){
    if(f.status && l.status !== f.status) return false;
    if(f.tier   && l.tier   !== f.tier)   return false;
    if(f.q){
      var hay = ((l.domain||"")+" "+(l.orgName||"")+" "+(l.licenseKey||"")).toLowerCase();
      if(hay.indexOf(f.q.toLowerCase()) < 0) return false;
    }
    return true;
  });

  function copyText(text, label){
    if(typeof navigator==="undefined"||!navigator.clipboard) return;
    navigator.clipboard.writeText(text).then(function(){ props.showToast && props.showToast(label+" copied"); });
  }

  var columns = [
    { key:"org", label:"Org / Domain", render:function(l){
        return E("div",null,
          E("div",{style:{fontWeight:700}}, l.orgName||l.domain),
          E("div",{style:{fontSize:11,color:P.slateM,fontFamily:FC}}, l.domain));
      } },
    { key:"tier", label:"Tier", render:function(l){
        return E(Chip,{tier:l.tier,size:"sm"}, (l.tier||"?").toUpperCase());
      } },
    { key:"validity", label:"Valid until", render:function(l){ return validityChip(l); } },
    { key:"status", label:"Status", render:function(l){
        return E(Chip,{variant:l.status==="active"?"emerald":"coral",size:"sm"},
          l.status||"?");
      } },
    { key:"key", label:"Key", mono:true, render:function(l){
        /* Only show the copy button when there's actually a key to copy.
           Pre-v1.1 license docs (no licenseKey field) render an em-dash
           with no affordance — clicking the icon used to copy the dash. */
        if (!l.licenseKey) {
          return E("span",{style:{color:P.slateM},
            title:"This license predates v1.1. Run the migration to assign a key, or use Regenerate key."},
            "—");
        }
        var k = l.licenseKey;
        return E("span",{style:{display:"inline-flex",alignItems:"center",gap:4}},
          E("span",{title:k}, maskKey(k)),
          E("button",{type:"button","aria-label":"Copy license key for "+l.domain,
            onClick:function(e){e.stopPropagation();copyText(k,"License key")},
            style:{background:"transparent",border:"none",cursor:"pointer",
              padding:4,color:P.slateM,borderRadius:4,minWidth:28,minHeight:28,
              display:"inline-flex",alignItems:"center"}},
            E(Copy,{size:13})));
      } },
    { key:"usage", label:"Scans", render:function(l){
        var used = l.scansUsedThisYear||0, cap = l.scansPerYear||0;
        var pct = cap>0?Math.min(100, Math.round(used/cap*100)):0;
        var col = pct>=90?P.coral:pct>=70?P.amber:P.emerald;
        return E("div",{style:{minWidth:120}},
          E("div",{style:{fontSize:11,color:P.slateM,marginBottom:3}}, used+" / "+(cap||"∞")),
          E("div",{role:"progressbar","aria-valuenow":pct,"aria-valuemin":0,"aria-valuemax":100,
            "aria-label":"Scans used: "+pct+"%",
            style:{height:6,background:P.bgS,borderRadius:3,overflow:"hidden"}},
            E("div",{style:{width:pct+"%",height:"100%",background:col}})));
      } },
    { key:"actions", label:"", align:"right", render:function(l){
        var isOpen = openId===l.id;
        return E("div",{style:{display:"inline-flex",gap:4}},
          E("button",{type:"button",
            "aria-expanded":isOpen?"true":"false",
            "aria-label":"Toggle details for "+l.domain,
            onClick:function(e){ e.stopPropagation(); setOpenId(isOpen?null:l.id); },
            style:{padding:"6px 10px",borderRadius:6,
              border:"1px solid "+P.bdr,background:P.card,fontSize:11,fontWeight:600,
              cursor:"pointer",minHeight:32}},
            isOpen?"Hide":"Details"),
          l.status==="active"
            ? E("button",{type:"button","aria-label":"Suspend license for "+(l.domain||l.code||l.id),
                onClick:function(e){e.stopPropagation();props.onSuspend&&props.onSuspend(l)},
                style:{padding:"6px 10px",borderRadius:6,border:"none",
                  background:P.coralLt,color:P.coralDk||P.coral,fontSize:11,fontWeight:700,
                  cursor:"pointer",minHeight:32,display:"inline-flex",alignItems:"center",gap:4}},
                E(Pause,{size:12}),"Suspend")
            : E("button",{type:"button","aria-label":"Resume license for "+(l.domain||l.code||l.id),
                onClick:function(e){e.stopPropagation();props.onActivate&&props.onActivate(l)},
                style:{padding:"6px 10px",borderRadius:6,border:"none",
                  background:P.emeraldLt,color:P.emeraldDk||P.emerald,fontSize:11,fontWeight:700,
                  cursor:"pointer",minHeight:32,display:"inline-flex",alignItems:"center",gap:4}},
                E(Play,{size:12}),"Resume"),
          /* PR-5.9: hard delete with typed-confirmation. Behind a
             coral icon button to signal destructive intent. The
             actual delete fires only after the user types the code
             (or "DELETE") in the modal. */
          E("button",{type:"button","aria-label":"Delete license "+(l.code||l.domain||l.id),
            onClick:function(e){e.stopPropagation();setTyped("");setDelTarget(l);},
            style:{padding:"6px 10px",borderRadius:6,border:"1px solid "+P.coral,
              background:"transparent",color:P.coralDk||P.coral,fontSize:11,fontWeight:700,
              cursor:"pointer",minHeight:32,display:"inline-flex",alignItems:"center",gap:4}},
            E(Trash,{size:12}),"Delete"));
      } }
  ];

  /* Filter bar */
  var filterBar = E("div",{role:"search","aria-label":"Filter licenses",
    style:{display:"flex",flexWrap:"wrap",gap:8,padding:"12px 14px",
      borderRadius:12,background:P.bgS,border:"1px solid "+P.bdr,marginBottom:14,
      position:"sticky",top:0,zIndex:5}},
    E("label",{style:{display:"flex",alignItems:"center",gap:6,
      background:P.card,padding:"0 10px",borderRadius:8,border:"1px solid "+P.bdr,
      flex:"1 1 240px",minHeight:36}},
      E(Search,{size:14}),
      E("span",{style:{position:"absolute",width:1,height:1,padding:0,margin:-1,overflow:"hidden",
        clip:"rect(0,0,0,0)",border:0}},"Search licenses"),
      E("input",{type:"text",value:f.q,onChange:function(e){update({q:e.target.value})},
        placeholder:"Search org, domain, key…",
        style:{border:"none",outline:"none",fontSize:12,flex:1,padding:"8px 0",
          background:"transparent",minHeight:36,fontFamily:"inherit",minWidth:0}})),
    E("label",{style:{display:"flex",alignItems:"center",gap:6}},
      E("span",{style:{fontSize:11,fontWeight:700,color:P.slateM}},"Status:"),
      E("select",{value:f.status,onChange:function(e){update({status:e.target.value})},
        style:{padding:"6px 10px",borderRadius:8,border:"1px solid "+P.bdr,fontSize:12,minHeight:36,background:P.card}},
        E("option",{value:""},"All"),
        E("option",{value:"active"},"Active"),
        E("option",{value:"suspended"},"Suspended"))),
    E("label",{style:{display:"flex",alignItems:"center",gap:6}},
      E("span",{style:{fontSize:11,fontWeight:700,color:P.slateM}},"Tier:"),
      E("select",{value:f.tier,onChange:function(e){update({tier:e.target.value})},
        style:{padding:"6px 10px",borderRadius:8,border:"1px solid "+P.bdr,fontSize:12,minHeight:36,background:P.card}},
        E("option",{value:""},"All"),
        E("option",{value:"free"},"Free"),
        E("option",{value:"starter"},"Starter"),
        E("option",{value:"standard"},"Standard"),
        E("option",{value:"enterprise"},"Enterprise"))),
    E("div",{style:{flex:"1 0 auto"}}),
    E("button",{type:"button",onClick:props.onRefresh,
      style:{padding:"8px 12px",borderRadius:8,border:"1px solid "+P.bdr,
        background:P.card,fontSize:11,fontWeight:600,cursor:"pointer",minHeight:36,
        display:"inline-flex",alignItems:"center",gap:6}},
      E(Refresh,{size:13}),"Refresh"),
    E("button",{type:"button",onClick:props.onCreate,
      style:{padding:"8px 12px",borderRadius:8,border:"none",
        background:P.pri,color:"#fff",fontSize:11,fontWeight:700,cursor:"pointer",minHeight:36,
        display:"inline-flex",alignItems:"center",gap:6}},
      E(Plus,{size:13}),"New license")
  );

  /* Body */
  var body;
  if(loading)        body = E(Skeleton,{rows:6,height:24,label:"Loading licenses…"});
  else if(error)     body = E(ErrorState,{title:"We couldn't load licenses.",
                              message:error, onRetry:props.onRefresh});
  else if(licenses.length===0)
                     body = E(EmptyState,{icon:E(CreditCard,{size:32}),
                              title:"No licenses yet.",
                              message:"Licenses are auto-provisioned at the free tier when domains are scanned, or you can create one manually.",
                              action:E("button",{type:"button",onClick:props.onCreate,
                                style:{padding:"8px 14px",borderRadius:8,border:"none",
                                  background:P.pri,color:"#fff",fontSize:11,fontWeight:700,
                                  cursor:"pointer",minHeight:36,display:"inline-flex",alignItems:"center",gap:6}},
                                E(Plus,{size:13}),"Create license")});
  else if(rows.length===0)
                     body = E(EmptyState,{title:"No licenses match the current filters.",
                              message:"Adjust the search or filter chips above."});
  else               body = E(DataTable,{columns:columns,rows:rows,
                              rowKey:function(l){return l.id||l.domain}});

  /* Optional row-detail panel (expand-in-place rather than a drawer for now). */
  var detail = null;
  if(openId){
    var l = rows.find(function(x){return (x.id||x.domain)===openId});
    if(l){
      detail = E("div",{style:{marginTop:14,padding:16,borderRadius:12,
        background:P.bgS,border:"1px solid "+P.bdr}},
        E("h3",{style:{margin:"0 0 10px",fontSize:13,fontWeight:800,color:P.slate}},
          (l.orgName||l.domain)+" — details"),
        E("dl",{style:{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",
          gap:10,margin:0}},
          [
            ["Domain", l.domain],
            ["Tier", (l.tier||"?").toUpperCase()],
            ["Status", l.status||"—"],
            ["Pages / domain", (l.pagesPerDomain||l.monthlyQuota||"—")],
            ["Scans / year", (l.scansPerYear||"—")],
            ["Scans used", (l.scansUsedThisYear||0)],
            ["Domains registered", (l.domainsRegistered||[]).join(", ") || "—"],
            ["License key", maskKey(l.licenseKey||l.id)],
            ["Created", (l.createdAt||"—").split("T")[0]],
            ["Updated", (l.updatedAt||"—").split("T")[0]]
          ].map(function(row,i){
            return E("div",{key:i},
              E("dt",{style:{fontSize:10,fontWeight:700,color:P.slateM,textTransform:"uppercase",marginBottom:2}}, row[0]),
              E("dd",{style:{fontSize:12,color:P.slate,margin:0,fontFamily:row[0]==="License key"||row[0]==="Domain"?FC:"inherit"}}, row[1]));
          })));
    }
  }

  /* PR-5.9: typed-confirmation modal for hard-delete. Renders only
     when delTarget is set. Confirmation requires the user to type
     either the license code or the literal string "DELETE" —
     destructive action, intentional friction, no muscle-memory
     mis-clicks. The actual delete call goes through props.onDelete
     so the parent's API wrapper handles the fetch + reload. */
  function confirmDelete(){
    if (!delTarget) return;
    setDelBusy(true);
    Promise.resolve(props.onDelete && props.onDelete(delTarget))
      .then(function(){ setDelBusy(false); setDelTarget(null); setTyped(""); })
      .catch(function(){ setDelBusy(false); });
  }
  var expectedToken = delTarget ? (delTarget.code || "DELETE") : "";
  var canDelete = !!delTarget && typed === expectedToken && !delBusy;
  var deleteModal = delTarget ? E(Modal, {
    open: true,
    onClose: function(){ if (!delBusy) { setDelTarget(null); setTyped(""); } },
    title: "Delete license",
    size: "md",
    footer: [
      E("button", { key:"cancel", type:"button",
        disabled: delBusy,
        onClick: function(){ setDelTarget(null); setTyped(""); },
        style: { padding:"8px 14px", borderRadius:8, border:"1px solid "+P.bdr,
          background:P.card, fontSize:11, fontWeight:600,
          cursor: delBusy ? "wait" : "pointer", minHeight:36 } }, "Cancel"),
      E("button", { key:"delete", type:"button",
        disabled: !canDelete,
        onClick: confirmDelete,
        style: { padding:"8px 14px", borderRadius:8, border:"none",
          background: canDelete ? P.coral : P.slateM, color:"#fff",
          fontSize:11, fontWeight:700,
          cursor: canDelete ? "pointer" : "not-allowed", minHeight:36 } },
        delBusy ? "Deleting…" : "Delete forever")
    ]
  },
    E("div", { style:{ display:"flex", gap:12, padding:"12px 14px", borderRadius:8,
      background:P.coralLt, border:"1px solid "+P.coral+"40",
      color:P.coralDk||P.coral, alignItems:"flex-start", marginBottom:14 } },
      E(AlertTriangle, { size:18 }),
      E("div", { style:{ fontSize:12, lineHeight:1.5 } },
        E("strong", null, "This cannot be undone."),
        " The license doc is removed from Cosmos permanently. ",
        "Bound users will lose product entitlements on their next sign-in. ",
        "If you only want to pause access, use Suspend instead.")),
    E("dl", { style:{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, margin:"0 0 14px" } },
      E("div", null,
        E("dt", { style:{ fontSize:10, fontWeight:700, color:P.slateM, textTransform:"uppercase" } }, "Preset"),
        E("dd", { style:{ fontSize:12, color:P.slate, margin:0 } }, delTarget.preset || delTarget.tier || "—")),
      E("div", null,
        E("dt", { style:{ fontSize:10, fontWeight:700, color:P.slateM, textTransform:"uppercase" } }, "Scope"),
        E("dd", { style:{ fontSize:12, color:P.slate, margin:0 } },
          (delTarget.scope && (delTarget.scope.kind + " · " + delTarget.scope.key))
          || delTarget.domain || "—")),
      E("div", { style:{ gridColumn:"1 / -1" } },
        E("dt", { style:{ fontSize:10, fontWeight:700, color:P.slateM, textTransform:"uppercase" } }, "License code"),
        E("dd", { style:{ fontSize:12, color:P.slate, margin:0, fontFamily:FC, wordBreak:"break-all" } },
          delTarget.code || "(no code — pre-v2 license)"))),
    E("div", null,
      E("label", { htmlFor:"lic-del-confirm",
        style:{ display:"block", fontSize:11, fontWeight:700, color:P.slateM, marginBottom:6 } },
        "Type ", E("code", { style:{ fontFamily:FC, background:P.bgS, padding:"1px 6px", borderRadius:4 } },
          expectedToken), " to confirm"),
      E("input", { id:"lic-del-confirm", type:"text",
        autoFocus: true, autoComplete: "off",
        value: typed,
        onChange: function(e){ setTyped(e.target.value); },
        style: { width:"100%", padding:"10px 12px", borderRadius:8,
          border:"1px solid "+(typed && !canDelete ? P.coral : P.bdr),
          fontSize:13, fontFamily:FC, minHeight:40,
          background:P.card, color:P.slate } }))
  ) : null;

  return E("div",null, filterBar, body, detail, deleteModal);
}
